import type {FetchWrapper} from '@/ts/api/fetch-wrapper';
import type {CompleteSignUpRequest, RegistrationStatusResponse} from '@/ts/types/dto/sign-in-dto';
import type {UserResponse} from '@/ts/types/dto/user-dto';

/**
 * Sign-up endpoints for an identity that is already authenticated in Zitadel but may not have a
 * Balm account yet — the Google sign-in path (IPD-2844).
 *
 * These live under `/public` on the BE (that prefix is exempt from the user lookup in the auth
 * filter, so a missing Balm account cannot produce a 401), but they still need the bearer token:
 * the BE reads the Zitadel user id from the JWT. Hence a wrapper of their own, sending auth
 * headers but *not* wired to the forced-logout handler — `registration-status` answering 401
 * must reach the caller instead of tearing the session down.
 *
 * {@link https://api-dev.ip.significo.dev/swagger-ui/index.html?urls.primaryName=public|Public API}
 */
export class SignUpApi {
  /**
   * @param probeFetch answers `registration-status`, whose 401 the caller has to see: it is how an
   *   identity with no Balm account is told apart from an expired session, so it must not log out.
   * @param signUpFetch answers `complete-signup`, whose 401 is an ordinary dead session and is
   *   handled like every other authenticated call.
   */
  public constructor(
    private readonly probeFetch: FetchWrapper,
    private readonly signUpFetch: FetchWrapper,
  ) {
  }

  /** Whether the authenticated Zitadel identity already has a Balm account. */
  public async getRegistrationStatus(): Promise<RegistrationStatusResponse> {
    return this.probeFetch.get({
      url: '/registration-status',
    });
  }

  /** Creates the Balm account (DB row, consents, contract) for an identity that already exists in Zitadel. */
  public async completeSignUp(body: CompleteSignUpRequest): Promise<UserResponse> {
    return this.signUpFetch.post({
      url: '/complete-signup',
      body,
    });
  }
}
